import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouterState } from '@tanstack/react-router'
import { Calendar, Check, FolderInput, Signal, UserPlus, X } from 'lucide-react'
import { useStatuses } from '../hooks/useWorkspaceStatuses'
import { useProjects } from '../hooks/useProjects'
import { SchedulePanel } from './TaskActionMenus'
import { AssigneeComboBox } from './AssigneeComboBox'
import { ProjectComboBox } from './ProjectComboBox'
import { PriorityComboBox } from './PriorityComboBox'
import { BulkLabelPicker } from './BulkLabelPicker'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  PICKER_SIDE_OFFSET,
  pickerPopoverClassName,
} from '@/components/app/combo-box/PickerPanel'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import { cn } from '@/lib/utils'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { useTaskSelectionStore } from '@/store/TaskSelectionStore'

/**
 * Bulk triage bar (design 13a): appears when rows are selected, applies one
 * change to all of them. Escape or navigating away clears the selection.
 */
export function BulkActionBar() {
  const { t } = useTranslation('tasks')
  const selectedIds = useTaskSelectionStore((s) => s.selectedIds)
  const clear = useTaskSelectionStore((s) => s.clear)
  const { updateTask } = useTaskMutations()
  const { all } = useStatuses()
  const { isPersonal } = useWorkspaceMode()
  const { projects } = useProjects()
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => {
    clear()
  }, [pathname, clear])

  useEffect(() => {
    if (selectedIds.size === 0) return
    const onKey = (e: KeyboardEvent) => {
      // An open picker handles its own Escape (Radix marks it defaultPrevented).
      if (e.key === 'Escape' && !e.defaultPrevented) clear()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedIds.size, clear])

  if (selectedIds.size === 0) return null

  const apply = (updates: Partial<TaskDocument>) => {
    for (const id of selectedIds) void updateTask(id, updates)
    clear()
  }
  const completedStatusId = all.statuses.find((s) => s.progressType === PROGRESS_TYPES.COMPLETED)?.id

  const buttonClass =
    'flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] text-white/80 transition-colors hover:bg-white/10 hover:text-white'
  // The pickers' own trigger is an outline button; make it one of the bar's buttons.
  const pickerButtonClass = cn(
    buttonClass,
    'w-auto justify-start border-0 bg-transparent font-normal shadow-none data-[state=open]:border-0 data-[state=open]:shadow-none',
  )

  return (
    <div
      role="toolbar"
      aria-label={t('bulk.toolbar')}
      className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-0.5 rounded-xl bg-[#1f1d1a] p-1.5 text-white shadow-2xl dark:bg-[#0f0e0d] dark:ring-1 dark:ring-white/10"
    >
      <span className="px-2.5 text-[13px] font-semibold">{t('bulk.selected', { count: selectedIds.size })}</span>
      <div className="mx-1 h-5 w-px bg-white/20" />

      <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <PopoverTrigger className={buttonClass}>
          <Calendar className="size-3.5" />
          {t('bulk.schedule')}
        </PopoverTrigger>
        <PopoverContent side="top" align="start" sideOffset={PICKER_SIDE_OFFSET} className={pickerPopoverClassName}>
          <SchedulePanel
            onApply={(updates) => {
              setScheduleOpen(false)
              apply(updates)
            }}
          />
        </PopoverContent>
      </Popover>

      <ProjectComboBox
        projects={projects}
        selectedProjectId={undefined}
        onProjectChange={(projectId) => apply({ projectId })}
        searchText={t('bulk.move')}
        buttonClassName={pickerButtonClass}
        triggerContent={
          <>
            <FolderInput className="size-3.5" />
            {t('bulk.move')}
          </>
        }
      />

      {!isPersonal && (
        // Tasks can span projects, so the Super Agent isn't offered here.
        <AssigneeComboBox
          selectedUserId={undefined}
          onUserChange={(userId) => apply({ userId })}
          buttonClassName={pickerButtonClass}
          triggerContent={
            <>
              <UserPlus className="size-3.5" />
              {t('bulk.assign')}
            </>
          }
        />
      )}

      <PriorityComboBox
        // Selected tasks can differ, so no row is checked.
        selectedPriority={null}
        onPriorityChange={(priority) => apply({ priority })}
        variant="icon-only"
        fontSize="sm"
        buttonClassName={cn(pickerButtonClass, 'focus:ring-0 focus:ring-offset-0')}
        triggerProps={{ title: t('bulk.priority') }}
        triggerContent={
          <>
            <Signal className="size-3.5" />
            {t('bulk.priority')}
          </>
        }
      />

      <BulkLabelPicker
        taskIds={selectedIds}
        projects={projects}
        buttonClassName={buttonClass}
        onApplied={clear}
      />

      {completedStatusId && (
        <button
          type="button"
          onClick={() => apply({ statusId: completedStatusId })}
          className="ml-1 flex h-8 items-center gap-1.5 rounded-md bg-brand px-3 text-[13px] font-semibold text-brand-foreground transition-opacity hover:opacity-90"
        >
          <Check className="size-3.5" />
          {t('bulk.done')}
        </button>
      )}
      <button
        type="button"
        onClick={clear}
        aria-label={t('bulk.clear')}
        title={t('bulk.clear')}
        className="ml-0.5 flex size-8 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}
